import React, { useCallback } from "react";
import { NavLink, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  FileStack,
  ClipboardCheck,
  BookOpen,
  FileSearch,
  Undo2,
  Scale,
  Plug,
  Settings as SettingsIcon,
  Command,
  Search,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import CommandPalette, { useCommandPalette } from "@/components/CommandPalette";

const NAV = [
  { to: "/app", end: true, label: "Overview", icon: LayoutGrid },
  { to: "/app/artifacts", label: "Candidate Artifacts", icon: FileStack },
  { to: "/app/reviews", label: "Reviews", icon: ClipboardCheck },
  { to: "/app/published", label: "Published Knowledge", icon: BookOpen },
  { to: "/app/evidence", label: "Evidence Console", icon: FileSearch },
  { to: "/app/revocations", label: "Revocations", icon: Undo2 },
  { to: "/app/policies", label: "Policies", icon: Scale },
  { to: "/app/integrations", label: "Integrations", icon: Plug },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon },
];

const CRUMBS = {
  "/app": "Overview",
  "/app/artifacts": "Candidate Artifacts",
  "/app/reviews": "Reviews",
  "/app/published": "Published Knowledge",
  "/app/evidence": "Evidence Console",
  "/app/revocations": "Revocations",
  "/app/policies": "Policies",
  "/app/integrations": "Integrations",
  "/app/settings": "Settings",
};

export default function AppLayout() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { open, setOpen } = useCommandPalette();

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem("pm_user") || "{}"); } catch { return {}; }
  })();

  function handleLogout() {
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_user");
    navigate("/login", { replace: true });
  }
  const currentLabel =
    Object.entries(CRUMBS)
      .sort((a, b) => b[0].length - a[0].length)
      .find(([k]) => loc.pathname === k || loc.pathname.startsWith(k + "/"))?.[1] || "Overview";

  const onAction = useCallback((kind) => {
    // Broadcast to current page (e.g. ArtifactReview listens via window event)
    window.dispatchEvent(new CustomEvent("proofmark:cmd", { detail: { kind } }));
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="app-layout">
      <CommandPalette open={open} onOpenChange={setOpen} onAction={onAction} />
      <aside className="w-64 shrink-0 border-r border-border/80 bg-[hsl(var(--background))] sticky top-0 h-screen flex flex-col">
        <Link to="/" className="flex items-center gap-2 px-5 py-5 border-b border-border/80" data-testid="app-brand-home">
          <LogoMark />
          <span className="font-serif-display text-lg">ProofMark</span>
        </Link>

        <div className="px-4 py-3 border-b border-border/80">
          <button
            onClick={() => setOpen(true)}
            data-testid="sidebar-open-cmdk"
            className="w-full flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm text-muted-foreground hover:bg-muted transition-colors"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search artifacts, evidence…</span>
            <kbd className="hidden md:inline-flex items-center gap-0.5 font-mono text-[10px] text-muted-foreground">
              <Command className="h-3 w-3" />K
            </kbd>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          <div className="tracked-caps px-3 pb-2">Workspace</div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-secondary text-primary"
                    : "text-foreground/80 hover:bg-muted hover:text-foreground",
                )
              }
            >
              <n.icon className="h-4 w-4 opacity-80" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border/80 px-3 pt-3 pb-2">
          <div className="rounded-md border border-brand/25 bg-brand/5 p-3">
            <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Live demo</div>
            <p className="text-xs text-foreground/80 leading-relaxed mb-3">Ready to govern your team&apos;s knowledge? Get ProofMark for your org.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1 rounded-md bg-brand text-brand-foreground px-2.5 py-1.5 text-[11px] font-medium hover:opacity-90 transition-opacity"
            >
              Request access
            </Link>
          </div>
        </div>

        <div className="border-t border-border/80 p-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-brand text-brand-foreground flex items-center justify-center font-mono text-xs shrink-0">
              {(currentUser.display_name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate">{currentUser.display_name || "—"}</div>
              <div className="text-[11px] text-muted-foreground truncate capitalize">{currentUser.role || "viewer"}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-10 bg-background/90 backdrop-blur border-b border-border/80">
          <div className="flex items-center justify-between px-8 py-4">
            <div className="flex items-baseline gap-3">
              <span className="tracked-caps">ProofMark</span>
              <span className="text-muted-foreground/60">/</span>
              <h1 className="font-serif-display text-xl leading-none">{currentLabel}</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="header-workspace-switcher"
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted transition-colors"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
                Acme Fintech · Production
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-md bg-brand text-brand-foreground px-3 py-1.5 text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Get access
              </Link>
            </div>
          </div>
        </header>
        <div className="flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export function LogoMark() {
  return (
    <svg width="26" height="26" viewBox="0 0 28 28" fill="none" aria-hidden>
      <rect x="1" y="1" width="26" height="26" rx="5" stroke="hsl(var(--brand))" strokeWidth="1.5" />
      <path d="M7 17 L13 11 L17 15 L21 11" stroke="hsl(var(--brand))" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="7" cy="17" r="1.3" fill="hsl(var(--brand))" />
      <circle cx="13" cy="11" r="1.3" fill="hsl(var(--brand))" />
      <circle cx="17" cy="15" r="1.3" fill="hsl(var(--brand))" />
      <circle cx="21" cy="11" r="1.3" fill="hsl(var(--brand))" />
    </svg>
  );
}
